import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, Phone } from "lucide-react";
import { scrollToId } from "./SmoothScroll";
import { PHONE_DISPLAY, PHONE_TEL, HERO_IMAGE } from "../lib/site";

const LINES = [
  { text: "MATLOCK", cls: "text-white" },
  { text: "GROUNDWORKS", cls: "text-white" },
  { text: "& DRAINAGE", cls: "text-ember" },
];

const STATS = ["Mon–Sat · 7:30–17:30", "Fully insured", "Derbyshire & Peak District"];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative flex min-h-screen flex-col overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110" aria-hidden="true">
        <img src={HERO_IMAGE} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/70" />
        <div className="absolute inset-x-0 bottom-0 h-72 bg-gradient-to-t from-coal-900 to-transparent" />
      </motion.div>

      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 flex flex-1 flex-col justify-end px-5 pb-8 pt-36 md:px-12 md:pb-10"
      >
        <div className="mb-6 flex items-center gap-3 overflow-hidden md:mb-8">
          <motion.span
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="h-[2px] w-12 origin-left bg-ember"
            aria-hidden="true"
          />
          <motion.p
            initial={{ opacity: 0, x: -16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.25 }}
            className="font-mono text-[11px] uppercase tracking-[0.25em] text-steel md:text-xs"
          >
            Ground specialists — Derbyshire, UK
          </motion.p>
        </div>

        <h1 data-testid="hero-heading" className="font-display uppercase leading-[0.85] tracking-tight">
          {LINES.map((line, i) => (
            <span key={line.text} className="block overflow-hidden">
              <motion.span
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.1, delay: 0.2 + i * 0.13, ease: [0.16, 1, 0.3, 1] }}
                className={`block text-[clamp(3.2rem,12.5vw,11.5rem)] ${line.cls}`}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-8 flex flex-col gap-6 md:mt-10 md:flex-row md:items-end md:justify-between">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-md text-sm leading-relaxed text-steel md:text-base"
          >
            Excavations, foundations and full drainage — dug, laid and
            finished properly. Free site visits and fixed quotes from our
            base in Bonsall, across Matlock and the Peak District.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-wrap items-center gap-3"
          >
            <button
              data-testid="hero-quote-button"
              onClick={() => scrollToId("#quote")}
              className="group relative overflow-hidden bg-ember px-7 py-4 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-coal-900"
            >
              <span className="absolute inset-0 translate-y-full bg-white transition-transform duration-300 ease-out group-hover:translate-y-0" aria-hidden="true" />
              <span className="relative flex items-center gap-2">
                Request a free quote <ArrowUpRight size={14} />
              </span>
            </button>
            <a
              href={`tel:${PHONE_TEL}`}
              data-testid="hero-call-button"
              className="flex items-center gap-2 border border-white/25 px-7 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:border-ember hover:text-ember"
            >
              <Phone size={14} /> Call {PHONE_DISPLAY}
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.15 }}
          className="mt-10 grid grid-cols-1 divide-y divide-white/10 border-t border-white/10 font-mono text-[10px] uppercase tracking-[0.2em] text-steel sm:grid-cols-3 sm:divide-x sm:divide-y-0 md:mt-12 md:text-[11px]"
        >
          {STATS.map((s) => (
            <div key={s} className="flex items-center gap-3 py-4 sm:px-5 sm:first:pl-0">
              <span className="h-1.5 w-1.5 bg-ember" aria-hidden="true" />
              {s}
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 right-6 z-10 hidden md:right-12 lg:block"
        aria-hidden="true"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}>
          <ArrowDown size={18} className="text-ember" />
        </motion.div>
      </motion.div>
    </section>
  );
};
